import React, { useState, useEffect, useContext } from "react";
import DOMPurify from "dompurify";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  Button,
  Label,
  Row,
  Col,
  Input,
} from "reactstrap";
import Select from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import {
  ANSWER_TYPES,
  MULTI_CORRECT_NEGATIVE_OPTIONS,
} from "components/constant";
import { PREVIEW_LIMIT } from "constants/CommonConstants";
import { useDispatch, useSelector } from "react-redux";
import { getTagList } from "action/tags";
import { getQuestionBankList, getQuestionBankById } from "action/questionBank";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { addQuestion } from "action/question";


const initialFields = {
  tag: { value: "", error: "" },
  questionBank: { value: "", error: "" },
};

const ExistingQuestionModal = ({
  isOpen,
  toggleModal,
  onSave,
  questionBankID,
  language,
  levelOptions = [],
  alreadyAddedQuestionIds = [],
}) => {

  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const [fields, setFields] = useState(initialFields);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [searchState, setSearchState] = useState({
    query: "",
    level: null,
    type: null,
    filterParams: [],
  });
  const [showQuestionsTable, setShowQuestionsTable] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedOptions, setExpandedOptions] = useState({});
  const [loadingState, setLoadingState] = useState({
    table: false,
    search: false,
    save: false,
  });
  const [localAlreadyAdded, setLocalAlreadyAdded] = useState([]);
  const [previewData, setPreviewData] = useState({
    src: "",
    width: 0,
    height: 0,
  });
  const tagsData = useSelector((state) => state.tags.tagsList);
  const questionBankData = useSelector(
    (state) => state.questionBanks.questionBankList,
  );
  const getPlainText = (html) => {
    if (!html) return "";
    const el = document.createElement("div");
    el.innerHTML = html;
    return el.textContent || "";
  };

  useEffect(() => {
    if (isOpen) {
      getTagList(dispatch);
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFields(initialFields);
      setShowQuestionsTable(false);
      setSelectedQuestions([]);
      setQuestions([]);

      setSearchState({
        query: "",
        level: null,
        type: null,
        filterParams: [],
      });

      setPreviewData({
        src: "",
        width: 0,
        height: 0,
      });

      setLocalAlreadyAdded([]);
    } else if (questionBankID) {
      fetchCurrentQB();
    }
  }, [isOpen, questionBankID]);

  useEffect(() => {
    if (showQuestionsTable && fields.questionBank.value) {
      fetchQuestions();
    }
  }, [searchState.filterParams, fields.questionBank.value]);

  const fetchCurrentQB = async () => {
    try {
      const res = await getQuestionBankById(questionBankID, null, []);

      if (res?.code === 200) {
        const texts =
          res.data.Question?.map((q) => {
            const qText =
              typeof q.question === "object"
                ? q.question?.en || q.question?.hi || ""
                : q.question || "";

            return getPlainText(qText).trim().toLowerCase();
          }).filter(Boolean) || [];

        setLocalAlreadyAdded(texts);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to fetch current question bank questions";

        notify("danger", message);
      }
    } catch (err) {
      notify(
        "danger",
        err?.message || "Failed to fetch current question bank questions",
      );
    }
  };

  const truncateHtml = (html, limit) => {
    if (!html) return "";
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;
    const walk = (node) => {
      if (count >= limit) {
        node.parentNode?.removeChild(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = limit - count;
        if (node.textContent.length > remaining) {
          node.textContent = node.textContent.slice(0, remaining) + "...";
          count = limit;
        } else {
          count += node.textContent.length;
        }
      } else {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    Array.from(container.childNodes).forEach(walk);
    return container.innerHTML;
  };

  const tagOptions =
    tagsData?.data?.Tag?.filter((tag) => tag.status === "ACTIVE").map(
      (tag) => ({
        value: tag.ID,
        label: tag.name,
      }),
    ) || [];

  const handleSelectAll = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]); // unselect all
    } else {
      setSelectedQuestions(questions.map((q) => q.id)); // select all
    }
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.query?.trim()) {
      filterParams.push({
        field: "Question",
        condition: "like",
        filterValues: [searchState.query.trim()],
      });
    }

    if (searchState.level?.value) {
      filterParams.push({
        field: "Level",
        condition: "eq",
        filterValues: [searchState.level.label], // same as QuestionBank
      });
    }

    if (searchState.type?.value) {
      filterParams.push({
        field: "AnswerType",
        condition: "eq",
        filterValues: [searchState.type.value],
      });
    }

    return JSON.stringify(filterParams);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const hasFilters =
      searchState.query?.trim() ||
      searchState.level?.value ||
      searchState.type?.value;

    if (!hasFilters) {
      return;
    }
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setLoadingState((prev) => ({
        ...prev,
        search: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));
    }
  };

  const clearSearch = () => {
    setLoadingState((prev) => ({
      ...prev,
      table: true,
      search: false,
    }));

    setSearchState({
      query: "",
      level: null,
      type: null,
      filterParams: [],
    });
  };

  const handleTagChange = (selected) => {
    handleChange("tag", selected.value);
    handleChange("questionBank", "");

    if (showQuestionsTable) {
      setShowQuestionsTable(false);
      setSelectedQuestions([]);
      setQuestions([]);
    }

    const languageValue =
      language === "English"
        ? "ENGLISH"
        : language === "Hindi"
          ? "HINDI"
          : language === "English, Hindi"
            ? "ENGLISH_HINDI"
            : language;

    const filters = [
      {
        field: "TagName",
        condition: "eq",
        filterValues: [selected.label],
      }
    ];

    if (languageValue !== "ENGLISH_HINDI") {
      filters.push({
        field: "Language",
        condition: "eq",
        filterValues: [languageValue],
      });
    }

    getQuestionBankList(dispatch, 1, JSON.stringify(filters));
  };

  const questionBankOptions =
    questionBankData?.data?.QuestionBank?.filter(
      (item) => item.ID !== questionBankID && item.status === "ACTIVE",
    ).map((item) => ({
      value: item.ID,
      label: item.bankName,
    })) || [];

  const handleSelect = (id) => {
    setSelectedQuestions((prev) =>
      prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id],
    );
  };

  const handleChange = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        value,
        error: "",
      },
    }));
  };

  const isQuestionBankDisabled = !fields.tag.value;

  const fetchQuestions = async () => {
    try {
      setLoadingState((prev) => ({
        ...prev,
        table: true,
      }));

      const res = await getQuestionBankById(
        fields.questionBank.value,
        null,
        searchState.filterParams,
      );
      if (res?.code === 200) {
        let mappedQuestions =
          res.data.Question?.map((q) => ({
            id: q.ID,
            levelId: q.questionDifficultyLevelID,
            language: q.language,
            question: q.question,
            type: q.answerType,
            marks: q.marks,
            level: q.level || "N/A",
            options:
              q.Answer?.map((ans) => ({
                text: ans.answer,
                grade: ans.grade / 100,
              })) || [],
            answer:
              q.Answer?.map((ans, idx) => (ans.grade > 0 ? idx : -1)).filter(
                (idx) => idx !== -1,
              ) || [],
          })) || [];


        setQuestions(mappedQuestions);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to fetch question bank details";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong!");
    } finally {
      setLoadingState((prev) => ({
        ...prev,
        table: false,
        search: false,
      }));
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "tag":
        return !value ? "Please select tag" : "";

      case "questionBank":
        return !value ? "Please select question bank" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        error: getValidationError(name, value),
      },
    }));
  };

  const validateForm = () => {
    const updatedFields = { ...fields };

    Object.keys(updatedFields).forEach((key) => {
      updatedFields[key] = {
        ...updatedFields[key],
        error: getValidationError(key, updatedFields[key].value),
      };
    });

    setFields(updatedFields);

    return Object.values(updatedFields).every((item) => !item.error);
  };

  const processAnswers = (options, answerType, languageKey) => {
    let answers = options.map((opt) => ({
      answer: answerType === "TRUE_FALSE" ? opt.text : opt[languageKey],
      grade: Number((opt.grade * 100).toFixed(4)),
    }));

    if (answerType === "MULTIPLE_CHOICE") {
      const positiveAnswers = answers.filter((a) => a.grade > 0);
      const sum = positiveAnswers.reduce((acc, curr) => acc + curr.grade, 0);

      // Distribute any minor float precision difference to the last positive answer
      if (sum > 99 && sum !== 100) {
        const diff = 100 - sum;
        for (let i = answers.length - 1; i >= 0; i--) {
          if (answers[i].grade > 0) {
            answers[i].grade = Number((answers[i].grade + diff).toFixed(4));
            break;
          }
        }
      }
    }
    return answers;
  };

  const handleSave = async () => {
    if (!showQuestionsTable) {
      if (!validateForm()) {
        return;
      }
      setLoadingState((prev) => ({
        ...prev,
        save: true,
      }));

      try {
        await fetchQuestions();
        setShowQuestionsTable(true);
      } finally {
        setLoadingState((prev) => ({
          ...prev,
          save: false,
        }));
      }

      return;
    }

    const selectedData = questions.filter((q) =>
      selectedQuestions.includes(q.id),
    );

    if (selectedData.length === 0) {
      notify("danger", "Please select at least one question to add.");
      return;
    }

    if (!questionBankID) {
      onSave(selectedData);
      toggleModal();
      return;
    }

    const payload = {
      questionBankID,
      questionDifficultyLevelID: selectedData?.[0]?.levelId || 1,
      questions: [],
    };

    selectedData.forEach((q) => {
      payload.questions.push({
        language: q.language,
        question: q.question,
        marks: Number(q.marks),
        answerType: q.type,
        status: "ACTIVE",
        answers: processAnswers(q.options, q.type, "text"),
      });
    });

    try {
      setLoadingState((prev) => ({
        ...prev,
        save: true,
      }));
      const response = await addQuestion(payload);

      if (response?.code === 200) {
        notify("success", response?.message || "Questions added successfully");

        onSave();
        toggleModal();
      } else {
        const message = Array.isArray(response?.message)
          ? response.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : response?.message || "Failed to add questions";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to add questions");
    } finally {
      setLoadingState((prev) => ({
        ...prev,
        save: false,
      }));
    }
  };

  const handleImagePreview = (e) => {
    const link = e.target.closest("a");
    if (link) {
      e.preventDefault();
    }

    const img = e.target.closest("img");
    if (!img) return;

    e.preventDefault();
    e.stopPropagation();
    setPreviewData({
      src: img.src,
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  return (
    <>
      {isOpen && <div className="blur-overlay"></div>}

      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        backdrop={false}
        keyboard={false}
        className={`modal-dialog-centered ${
          showQuestionsTable
            ? "existing-question-modal-large"
            : "existing-question-modal-medium"
        }`}
      >
        <ModalHeader toggle={toggleModal}>
          <h4 className="mb-0 text-default section-title">
            Select Existing Question
          </h4>
        </ModalHeader>

        <ModalBody>
          <Card className="mb-4 shadow-sm border-0">
            <CardBody>
              <div className="d-flex flex-wrap align-items-start">
                <div className="mr-2 mb-3 mb-md-0">
                  <Label className="small-label text-muted">
                    Tag <span className="text-danger">*</span>
                  </Label>
                  <div className={fields.tag.error ? "has-danger" : ""}>
                    <Select
                      className="filter-select"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      options={tagOptions}
                      placeholder="Select tag"
                      value={
                        tagOptions.find(
                          (opt) => opt.value === fields.tag.value,
                        ) || null
                      }
                      onChange={(selected) => {
                        handleTagChange(selected);
                        if (showQuestionsTable) {
                          setShowQuestionsTable(false);
                          setSelectedQuestions([]);
                          setQuestions([]);
                        }
                      }}
                      onBlur={() => handleOnBlur("tag", fields.tag.value)}
                    />
                    {fields.tag.error && (
                      <div className="invalid-feedback d-block mt-2">
                        {fields.tag.error}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mr-2 mb-3 mb-md-0">
                  <Label className="small-label text-muted" >
                    Question Bank <span className="text-danger">*</span>
                  </Label>
                  <div
                    className={fields.questionBank.error ? "has-danger" : ""}
                  >
                    <Select
                      className="filter-select"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      options={questionBankOptions}
                      placeholder="Select question bank"
                      isDisabled={isQuestionBankDisabled}
                      value={
                        questionBankOptions.find(
                          (opt) => opt.value === fields.questionBank.value,
                        ) || null
                      }
                      onChange={(selected) => {
                        handleChange("questionBank", selected.value);
                        if (showQuestionsTable) {
                          setSelectedQuestions([]);
                        }
                      }}
                      onBlur={() =>
                        handleOnBlur("questionBank", fields.questionBank.value)
                      }
                    />
                    {fields.questionBank.error && (
                      <div className="invalid-feedback d-block mt-2">
                        {fields.questionBank.error}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {!showQuestionsTable && (
            <div className="d-flex justify-content-end mt-4">
              <Button
                color="secondary save-btn-sm"
                onClick={toggleModal}
                className="mr-2 px-4"
              >
                Cancel
              </Button>

              <Button
                color="deep-blue save-btn-sm"
                onClick={handleSave}
                className="px-4"
              >
                {loadingState.save ? (
                  <i className="fa fa-spinner fa-spin loader ml-12px" />
                ) : (
                  "Search"
                )}
              </Button>
            </div>
          )}
          {showQuestionsTable && (
            <>
              <Card>
                <Row className="mb-4 mt-4 ml-2">
                  <Col md="12">
                    <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                      <div className="d-flex flex-wrap align-items-center filter-group">
                        {/* Search */}
                        <Input
                          type="text"
                          placeholder="Search by question"
                          className="mr-2 mb-2 filter-input-large"
                          value={searchState.query}
                          onChange={(e) =>
                            setSearchState((prev) => ({
                              ...prev,
                              query: e.target.value,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        {/* Level */}
                        <Select
                          className="mr-2 mb-2 filter-select"
                          options={levelOptions}
                          placeholder="Select level"
                          styles={fixedWidthCustomStyle()}
                          value={searchState.level}
                          onChange={(selected) =>
                            setSearchState((prev) => ({
                              ...prev,
                              level: selected,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        {/* Answer Type */}
                        <Select
                          className="mr-2 mb-2 filter-select"
                          options={ANSWER_TYPES}
                          placeholder="Select option type"
                          styles={fixedWidthCustomStyle()}
                          value={searchState.type}
                          onChange={(selected) =>
                            setSearchState((prev) => ({
                              ...prev,
                              type: selected,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        <Button
                          className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${loadingState.search ? "search-btn" : ""}`}
                          onClick={handleSearch}
                          disabled={loadingState.search}
                        >
                          {loadingState.search ? (
                            <>
                              <i className="fa fa-spinner fa-spin loader" />
                            </>
                          ) : (
                            <>
                              <i className="fa fa-search mr-1" />
                              Search
                            </>
                          )}
                        </Button>

                        {searchState.filterParams.length > 0 && (
                          <Button
                            className="mb-2 filter-btn btn-deep-blue"
                            onClick={clearSearch}
                          >
                            <i className="fa fa-times mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <Label check className="mb-0 ml-4">
                    <Input
                      type="checkbox"
                      checked={
                        selectedQuestions.length === questions.length &&
                        questions.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                    <span className="ml-2">Select All</span>
                  </Label>
                  <h4 className="mb-0">
                    <h4 className="mb-0">
                      Selected Questions: {selectedQuestions.length} /{" "}
                      {questions.length}
                    </h4>
                  </h4>
                </CardHeader>

                <CardBody>
                  {loadingState.table ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : questions.length > 0 ? (
                    questions.map((q, index) => (
                      <Card key={q.id} className="mb-3">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-start flex-wrap question-header">
                            <div className="d-flex ml-2 flex-grow-1 question-content-wrapper">
                              <Input
                                type="checkbox"
                                className="mr-3 mt-1"
                                checked={selectedQuestions.includes(q.id)}
                                onChange={() => handleSelect(q.id)}
                              />

                              <div className="flex-grow-1 question-content-wrapper">
                                <div className="question-block mb-0">
                                  <div className="d-flex">
                                    <span className="mr-1 font-weight-bold">
                                      Q{index + 1}.
                                    </span>

                                    <div className="flex-grow-1">
                                      {(() => {
                                        let qText = typeof q.question === "object" ? q.question?.en || q.question?.hi || "" : q.question || "";
                                        const plainText = getPlainText(qText);
                                        const isExpanded = expandedQuestions[index];
                                        const shouldTrim = plainText.length > PREVIEW_LIMIT;
                                        const displayText = isExpanded || !shouldTrim ? qText : truncateHtml(qText, PREVIEW_LIMIT);

                                        return (
                                          <>
                                            <div
                                              className="question-html-content"
                                              onClick={handleImagePreview}
                                              dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(displayText),
                                              }}
                                            />
                                            {shouldTrim && (
                                              <div className="mt-1">
                                                <button
                                                  className="lesson-read-more-btn"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedQuestions((prev) => ({
                                                      ...prev,
                                                      [index]: !prev[index],
                                                    }));
                                                  }}
                                                >
                                                  {isExpanded ? "Read Less" : "Read More"}
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="question-meta flex-wrap justify-content-end ml-auto mt-2 mt-md-0">
                              <span className="question-type-badge">
                                {ANSWER_TYPES.find((item) => item.value === q.type)?.label ||
                                  q.type}
                              </span>

                              <span className="question-mark-badge">
                                Mark: {String(q.marks).padStart(2, "0")}
                              </span>

                              <span className="question-mark-badge">
                                Level: {levelOptions.find((item) => item.value === q.level)?.label || (q.level ? q.level.charAt(0).toUpperCase() + q.level.slice(1).toLowerCase() : "-")}
                              </span>
                            </div>
                          </div>

                          {/* SINGLE & MULTIPLE CHOICE */}
                          {(q.type === "SINGLE_CHOICE" ||
                            q.type === "MULTIPLE_CHOICE") && (
                            <Row className="mt-3">
                              {q.options &&
                                q.options.map((opt, i) => {
                                  const isCorrect =
                                    q.answer?.includes(i) ||
                                    (opt.grade !== undefined && opt.grade > 0);

                                  return (
                                    <Col md="6" key={i} className="mb-3">
                                      <div
                                        className={`question-option-card${
                                          isCorrect
                                            ? " option-correct"
                                            : " option-disabled"
                                        }`}
                                      >
                                        <div className="d-flex align-items-center w-100">
                                          <div
                                            className={`option-icon mr-3${
                                              isCorrect
                                                ? " text-success"
                                                : " text-muted"
                                            }`}
                                          >
                                            {q.type === "SINGLE_CHOICE" ? (
                                              <i
                                                className={`fa-${
                                                  isCorrect
                                                    ? "solid fa-circle-check text-success"
                                                    : "regular fa-circle text-muted"
                                                }`}
                                              />
                                            ) : (
                                              <i
                                                className={`fa-${
                                                  isCorrect
                                                    ? "solid fa-square-check text-success"
                                                    : "regular fa-square text-muted"
                                                }`}
                                              />
                                            )}
                                          </div>

                                          <div className="flex-grow-1">
                                            <div className="d-flex align-items-center w-100">
                                              <div className="flex-grow-1">
                                                {(() => {
                                                  const optText = opt.text || opt.textEnglish || opt.textHindi || "";
                                                  const plainOptText = getPlainText(optText);
                                                  const optKey = `${index}-${i}`;
                                                  const isOptExpanded = expandedOptions[optKey];
                                                  const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                                  const displayOptText = isOptExpanded || !shouldTrimOpt
                                                    ? optText
                                                    : truncateHtml(optText, PREVIEW_LIMIT);

                                                  return (
                                                    <>
                                                      <div
                                                        className="question-html-content flex-grow-1"
                                                        onClick={handleImagePreview}
                                                        dangerouslySetInnerHTML={{
                                                          __html: DOMPurify.sanitize(displayOptText),
                                                        }}
                                                      />
                                                      {shouldTrimOpt && (
                                                        <div className="mt-1">
                                                          <button
                                                            className="lesson-read-more-btn"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setExpandedOptions((prev) => ({
                                                                ...prev,
                                                                [optKey]: !prev[optKey],
                                                              }));
                                                            }}
                                                          >
                                                            {isOptExpanded ? "Read Less" : "Read More"}
                                                          </button>
                                                        </div>
                                                      )}
                                                    </>
                                                  );
                                                })()}
                                              </div>

                                              {opt.grade !== undefined &&
                                                opt.grade !== null && (
                                                  <span className="option-grade ml-2">
                                                    (
                                                    {MULTI_CORRECT_NEGATIVE_OPTIONS.find(
                                                      (g) =>
                                                        g.value === opt.grade,
                                                    )?.label || `${opt.grade * (opt.grade <= 1 ? 100 : 1)}%`}
                                                    )
                                                  </span>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                            </Row>
                          )}

                          {/* TRUE FALSE */}
                          {(q.type === "TF" || q.type === "TRUE_FALSE") && (
                            <Row className="mt-3">
                              {q.options &&
                                q.options.map((opt, i) => {
                                  const isCorrect =
                                    q.answer?.includes(i) ||
                                    q.answer?.[0] === i ||
                                    (opt.grade !== undefined && opt.grade > 0);

                                  return (
                                    <Col md="6" key={i} className="mb-3">
                                      <div
                                        className={`question-option-card${
                                          isCorrect
                                            ? " option-correct"
                                            : " option-disabled"
                                        }`}
                                      >
                                        <div className="d-flex align-items-center">
                                          <div
                                            className={`option-icon mr-3${
                                              isCorrect
                                                ? " text-success"
                                                : " text-muted"
                                            }`}
                                          >
                                            <i
                                              className={`fa-${
                                                isCorrect
                                                  ? "solid fa-circle-check text-success"
                                                  : "regular fa-circle text-muted"
                                              }`}
                                            />
                                          </div>

                                          <div className="flex-grow-1">
                                            {(() => {
                                              const optText = opt.text || opt.textEnglish || opt.textHindi || "";
                                              const plainOptText = getPlainText(optText);
                                              const optKey = `${index}-${i}`;
                                              const isOptExpanded = expandedOptions[optKey];
                                              const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                              const displayOptText = isOptExpanded || !shouldTrimOpt
                                                ? optText
                                                : truncateHtml(optText, PREVIEW_LIMIT);

                                              return (
                                                <>
                                                  <div
                                                    className="question-html-content flex-grow-1"
                                                    onClick={handleImagePreview}
                                                    dangerouslySetInnerHTML={{
                                                      __html: DOMPurify.sanitize(displayOptText),
                                                    }}
                                                  />
                                                  {shouldTrimOpt && (
                                                    <div className="mt-1">
                                                      <button
                                                        className="lesson-read-more-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExpandedOptions((prev) => ({
                                                            ...prev,
                                                            [optKey]: !prev[optKey],
                                                          }));
                                                        }}
                                                      >
                                                        {isOptExpanded ? "Read Less" : "Read More"}
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                            </Row>
                          )}

                          {/* IMAGE CHOICE */}
                          {q.type === "IMAGE_CHOICE" && (
                            <Row className="mt-3">
                              {q.options &&
                                q.options.map((opt, i) => {
                                  const isCorrect =
                                    q.answer?.includes(i) ||
                                    (opt.grade !== undefined && opt.grade > 0);

                                  return (
                                    <Col
                                      lg="3"
                                      md="4"
                                      sm="6"
                                      xs="12"
                                      key={i}
                                      className="mb-3"
                                    >
                                      <div
                                        className={`image-option-card${
                                          isCorrect
                                            ? " option-correct"
                                            : " option-disabled"
                                        }`}
                                      >
                                        {opt.image && (
                                          <div className="image-wrapper mb-2">
                                            <img
                                              src={opt.image}
                                              alt={`Option ${i + 1}`}
                                              className="img-fluid rounded"
                                            />
                                          </div>
                                        )}

                                        <div className="d-flex align-items-center">
                                          <div
                                            className={`option-icon mr-2${
                                              isCorrect
                                                ? " text-success"
                                                : " text-muted"
                                            }`}
                                          >
                                            <i
                                              className={`fa-${
                                                isCorrect
                                                  ? "solid fa-square-check text-success"
                                                  : "regular fa-square text-muted"
                                              }`}
                                            />
                                          </div>

                                          <div className="flex-grow-1">
                                            {(() => {
                                              const optText = opt.text || opt.textEnglish || opt.textHindi || "";
                                              const plainOptText = getPlainText(optText);
                                              const optKey = `${index}-${i}`;
                                              const isOptExpanded = expandedOptions[optKey];
                                              const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                              const displayOptText = isOptExpanded || !shouldTrimOpt
                                                ? optText
                                                : truncateHtml(optText, PREVIEW_LIMIT);

                                              return (
                                                <>
                                                  <div
                                                    className="question-html-content flex-grow-1"
                                                    onClick={handleImagePreview}
                                                    dangerouslySetInnerHTML={{
                                                      __html: DOMPurify.sanitize(displayOptText),
                                                    }}
                                                  />
                                                  {shouldTrimOpt && (
                                                    <div className="mt-1">
                                                      <button
                                                        className="lesson-read-more-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExpandedOptions((prev) => ({
                                                            ...prev,
                                                            [optKey]: !prev[optKey],
                                                          }));
                                                        }}
                                                      >
                                                        {isOptExpanded ? "Read Less" : "Read More"}
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                            </Row>
                          )}
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <div className="loaderContainer text-muted">
                      No questions found.
                    </div>
                  )}
                </CardBody>
              </Card>

              <div className="d-flex justify-content-end">
                <Button
                  color="secondary save-btn-sm"
                  onClick={toggleModal}
                  className="mr-2 px-4"
                >
                  Cancel
                </Button>

                <Button
                  color="deep-blue save-btn-sm"
                  onClick={handleSave}
                  className="px-4"
                >
                  {loadingState.save ? (
                    <i className="fa fa-spinner fa-spin loader ml-12px" />
                  ) : (
                    "Add"
                  )}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={!!previewData.src}
        toggle={() =>
          setPreviewData({
            src: "",
            width: 0,
            height: 0,
          })
        }
        size="xl"
        centered
      >
        <ModalHeader
          toggle={() =>
            setPreviewData({
              src: "",
              width: 0,
              height: 0,
            })
          }
        >
          Image Preview
        </ModalHeader>

        <ModalBody className="text-center">
          <img src={previewData.src} alt="Preview" className="preview-image" />
        </ModalBody>
      </Modal>
    </>
  );
};

export default ExistingQuestionModal;
